import { createException } from '@/utils/monitoring.utils.ts';
import * as Sentry from '@sentry/vue';

let isScriptLoaded = false;
let isWidgetInitialized = false;

export interface IFreshchatConfig {
  token: string;
  host: string;
  siteId: string;
}

function loadScript(): Promise<void> {
  return new Promise((resolve) => {
    if (isScriptLoaded) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'freshchat-js-sdk';
    script.src = 'https://wchat.freshchat.com/js/widget.js';
    script.async = true;
    script.onload = () => {
      isScriptLoaded = true;
      resolve();
    };
    script.onerror = (event, source, lineno, colno, error) => {
      createException(error ?? new Error('Failed to load Freshchat script'), {
        extra: { event, source, lineno, colno },
      });
      resolve(); // Resolve even on error to prevent blocking further execution
    };

    document.head.appendChild(script);
  });
}

function initWidget(config: IFreshchatConfig) {
  if (isWidgetInitialized) return;

  window.fcWidget.init({
    token: config.token,
    host: config.host,
    siteId: config.siteId,
  });

  isWidgetInitialized = true;
}

export async function setupFreshchat(config: IFreshchatConfig) {
  await loadScript();
  if (isScriptLoaded) initWidget(config);
}

export const isFreshChatWidgetIllegalInvocation = (event: Sentry.ErrorEvent) => {
  const exception = event.exception?.values?.[0];
  const frames = exception?.stacktrace?.frames ?? [];

  const isFromWidget = frames.some(
    (f) => f.filename?.includes('/js/widget.js') || f.filename?.includes('wchat.freshchat.com'),
  );
  const isIllegalInvocation = exception?.type === 'TypeError' && exception?.value === 'Illegal invocation';

  return isFromWidget && isIllegalInvocation;
};
